import { motion } from 'framer-motion';
import { ChevronRight, CornerDownRight } from 'lucide-react';
import { CARD_CLASS, EYEBROW_CLASS, KPI_HEALTH_COLOR, OMS_COLORS, sectionVariants, tint } from '../omsDashTheme';
import type { OmsDrillBucket, OmsLayerKpi, OmsLayeredOverview } from '../types';
import CalcHint from './CalcHint';
import StatCount from './StatCount';

interface Props {
  layered: OmsLayeredOverview | null;
  loading: boolean;
  onSelect: (bucket: OmsDrillBucket) => void;
}

/** Stage names owned by the dashboard so the pipeline reads unambiguously. */
const DISPLAY_LABEL: Partial<Record<OmsDrillBucket, string>> = {
  omsTotal: 'Total Projects',
  omsDrafts: 'Drafts',
  omsPendingApproval: 'Pending Approval',
  omsApproved: 'Approved',
  omsReadyToStart: 'Ready to Start',
  omsReadyToSendReview: 'Ready to Send Review',
  omsStarted: 'Started',
};

/** Rounded share-of-parent percentage; 0 when the parent is empty. */
const sharePct = (part: number, whole: number) =>
  whole > 0 ? Math.round((part / whole) * 100) : 0;

/** "51 of 56 approved = 91%" — the standard calculation line for a share. */
const shareLine = (part: number, whole: number, wholeLabel: string) =>
  `${part} of ${whole} ${wholeLabel} = ${sharePct(part, whole)}%`;

/**
 * One split row: dot + label on the left, count on the right, and a slim
 * share-of-parent bar with its percentage underneath. Clickable → drill;
 * hovering reveals the calculation behind the figure.
 */
function SplitRow({ kpi, parentValue, color, index, calc, onSelect }: {
  kpi: OmsLayerKpi<OmsDrillBucket>;
  parentValue: number;
  color: string;
  index: number;
  calc: string[];
  onSelect: (b: OmsDrillBucket) => void;
}) {
  const palette = KPI_HEALTH_COLOR[kpi.health];
  const pct = sharePct(kpi.value, parentValue);
  return (
    <motion.button
      type="button"
      custom={index}
      variants={sectionVariants}
      initial="hidden"
      animate="visible"
      onClick={() => onSelect(kpi.bucket)}
      className="group relative w-full rounded-xl px-2.5 py-2 text-left transition hover:bg-[rgba(11,59,46,0.05)]"
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2">
          <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
          <span className="truncate text-[11px] font-bold uppercase tracking-[0.12em]" style={{ color: OMS_COLORS.ink }}>
            {DISPLAY_LABEL[kpi.bucket] ?? kpi.label}
          </span>
        </span>
        <span className="shrink-0">
          {/* Stage name is the visible label to the left; the count still names its noun + parent. */}
          <StatCount label="" value={kpi.value} of={parentValue} noun="projects" size="md" animated align="end" ink={palette.ink} />
        </span>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <span className="h-1.5 flex-1 overflow-hidden rounded-full" style={{ backgroundColor: tint(color, 0.15) }}>
          <motion.span
            className="block h-full rounded-full"
            style={{ backgroundColor: color }}
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ delay: 0.25 + index * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          />
        </span>
        <span className="w-9 shrink-0 text-right text-[10px] font-bold tabular-nums" style={{ color: OMS_COLORS.caption }}>
          {pct}%
        </span>
      </div>
      <CalcHint lines={calc} />
    </motion.button>
  );
}

function ColumnHeading({ icon, children }: { icon?: 'flow' | 'branch'; children: React.ReactNode }) {
  return (
    <p className="flex items-center gap-1.5 px-2.5 text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: OMS_COLORS.caption }}>
      {icon === 'flow' && <ChevronRight aria-hidden className="h-3 w-3" style={{ color: OMS_COLORS.amber }} />}
      {icon === 'branch' && <CornerDownRight aria-hidden className="h-3 w-3" style={{ color: OMS_COLORS.amber }} />}
      {children}
    </p>
  );
}

/**
 * Portfolio pipeline as a left-to-right flow of complete partitions:
 * one engraved Total numeral, then "split of total"
 * (Approved + Pending Approval + Drafts = Total), then "split of approved"
 * (Started + Ready to Start = Approved) and the drafts follow-up
 * (Ready to Send Review, out of Drafts). Every figure carries a hover
 * popover showing the arithmetic behind it.
 */
export default function PipelineStrip({ layered, loading, onSelect }: Props) {
  if (loading && !layered) {
    return <div className={`${CARD_CLASS} h-[196px] animate-pulse`} />;
  }
  if (!layered) return null;

  const byBucket = new Map(layered.layer1.map((k) => [k.bucket, k]));
  const total = byBucket.get('omsTotal');
  const drafts = byBucket.get('omsDrafts');
  const pending = byBucket.get('omsPendingApproval');
  const approved = byBucket.get('omsApproved');
  const { active, readyToSendReview } = layered.layer2;

  const totalValue = total?.value ?? 0;
  const approvedValue = approved?.value ?? 0;
  const pendingValue = pending?.value ?? 0;
  const draftsValue = drafts?.value ?? 0;
  const startedValue = active.value;
  const reviewReadyValue = readyToSendReview.value;

  // Derive the Approved − Started remainder when the backend predates the
  // dedicated KPI, so Started + Ready to Start always tallies to Approved.
  const readyToStart: OmsLayerKpi<OmsDrillBucket> = layered.layer2.readyToStart ?? {
    bucket: 'omsReadyToStart',
    label: 'Ready to Start',
    value: Math.max(0, approvedValue - startedValue),
    description: 'Approved projects awaiting task generation',
    health: 'neutral',
  };
  const draftsInConfiguration = Math.max(0, draftsValue - reviewReadyValue);

  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      animate="visible"
      custom={1}
      aria-label="Project pipeline"
      className={`${CARD_CLASS} p-4`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={EYEBROW_CLASS}>Project pipeline · approval to execution</p>
        {layered.rejectedCount > 0 && (
          <span
            className="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide"
            style={{ color: OMS_COLORS.red, backgroundColor: tint(OMS_COLORS.red, 0.10) }}
          >
            {layered.rejectedCount} rejected project{layered.rejectedCount === 1 ? '' : 's'} excluded
          </span>
        )}
      </div>

      <div className="mt-3 grid gap-4 md:grid-cols-[200px_minmax(0,1fr)_minmax(0,1fr)] md:gap-0">
        {/* Total — the engraved anchor numeral */}
        <motion.button
          type="button"
          custom={0}
          variants={sectionVariants}
          initial="hidden"
          animate="visible"
          onClick={() => total && onSelect(total.bucket)}
          className="group relative flex flex-col justify-center rounded-xl px-3 py-2 text-left transition hover:bg-[rgba(11,59,46,0.04)] md:pr-5"
        >
          <span className="mt-1">
            <StatCount label={DISPLAY_LABEL.omsTotal ?? 'Total Projects'} value={totalValue} noun="projects" size="xl" animated />
          </span>
          <span className="mt-2 text-[10px] font-semibold" style={{ color: OMS_COLORS.caption }}>
            Active portfolio ·{' '}
            <span className="text-[#1F5A47] opacity-0 transition group-hover:opacity-100">view details →</span>
          </span>
          <CalcHint
            lines={[
              `Approved (${approvedValue}) + Pending Approval (${pendingValue}) + Drafts (${draftsValue}) = ${totalValue}`,
              ...(layered.rejectedCount > 0
                ? [`${layered.rejectedCount} rejected project${layered.rejectedCount === 1 ? '' : 's'} excluded from every figure`]
                : []),
            ]}
          />
        </motion.button>

        {/* Split of Total — Approved + Pending Approval + Drafts = Total */}
        <div className="flex flex-col gap-0.5 border-t border-[rgba(11,59,46,0.12)] pt-3 md:border-l md:border-t-0 md:px-3 md:pt-0">
          <ColumnHeading icon="flow">Split of total · {totalValue} projects</ColumnHeading>
          {approved && (
            <SplitRow
              kpi={approved}
              parentValue={totalValue}
              color={OMS_COLORS.mint}
              index={1}
              calc={[
                shareLine(approvedValue, totalValue, 'total projects'),
                `Started (${startedValue}) + Ready to Start (${readyToStart.value}) = ${approvedValue}`,
              ]}
              onSelect={onSelect}
            />
          )}
          {pending && (
            <SplitRow
              kpi={pending}
              parentValue={totalValue}
              color={OMS_COLORS.amber}
              index={2}
              calc={[
                shareLine(pendingValue, totalValue, 'total projects'),
                'Submitted for approval and awaiting a decision',
              ]}
              onSelect={onSelect}
            />
          )}
          {drafts && (
            <SplitRow
              kpi={drafts}
              parentValue={totalValue}
              color={OMS_COLORS.slate}
              index={3}
              calc={[
                shareLine(draftsValue, totalValue, 'total projects'),
                `Ready to Send Review (${reviewReadyValue}) + Still in configuration (${draftsInConfiguration}) = ${draftsValue}`,
              ]}
              onSelect={onSelect}
            />
          )}
        </div>

        {/* Split of Approved — Started + Ready to Start = Approved, then the drafts follow-up */}
        <div className="flex flex-col gap-0.5 border-t border-[rgba(11,59,46,0.12)] pt-3 md:border-l md:border-t-0 md:pl-3 md:pt-0">
          <ColumnHeading icon="branch">Split of approved · {approvedValue} projects</ColumnHeading>
          <SplitRow
            kpi={active}
            parentValue={approvedValue}
            color={OMS_COLORS.teal}
            index={4}
            calc={[
              shareLine(startedValue, approvedValue, 'approved'),
              'Approved projects with tasks generated',
            ]}
            onSelect={onSelect}
          />
          <SplitRow
            kpi={readyToStart}
            parentValue={approvedValue}
            color={OMS_COLORS.mint}
            index={5}
            calc={[
              shareLine(readyToStart.value, approvedValue, 'approved'),
              'Approved, but tasks not generated yet — awaiting kickoff',
            ]}
            onSelect={onSelect}
          />
          <ColumnHeading icon="branch">From drafts · {draftsValue} projects</ColumnHeading>
          <SplitRow
            kpi={readyToSendReview}
            parentValue={draftsValue}
            color={OMS_COLORS.champagneDeep}
            index={6}
            calc={[
              shareLine(reviewReadyValue, draftsValue, 'drafts'),
              `Fully configured, awaiting review submission · ${draftsInConfiguration} still in configuration`,
            ]}
            onSelect={onSelect}
          />
        </div>
      </div>
    </motion.section>
  );
}
